/**
 * Groups endpoints (FAME-243) — Costanza's thin policy layer over MUC room
 * state. Ids are room-JID localparts (`g-<uuid>`); callers build full room
 * JIDs with `buildRoomJid` and the tenant vhost.
 */
import type { ChatGroup } from "../types"
import { bearer, chatBaseUrl, chatFetch } from "./helpers"

type RawGroup = {
  id: string
  name: string
  owner: string | null
  admins: string[]
  members: string[]
}

const fromRaw = (raw: RawGroup): ChatGroup => ({
  id: raw.id,
  name: raw.name,
  owner: raw.owner,
  admins: raw.admins,
  members: raw.members,
})

/**
 * `POST /groups` — create a group. The caller becomes owner and member; every
 * member (caller included) gets a conversations row server-side, so the group
 * is listable before the first message.
 */
export const createGroup = (
  sessionToken: string,
  name: string,
  memberIds: string[],
  signal?: AbortSignal
) =>
  chatFetch<ChatGroup, "json">({
    baseUrl: chatBaseUrl(),
    path: "/groups",
    method: "POST",
    headers: bearer(sessionToken),
    data: { name, member_ids: memberIds },
    responseType: "json",
    transform: (data) => fromRaw(data as RawGroup),
    signal,
  })

/**
 * `GET /groups/:id` — one group's details. Misses are real errors: 404 for a
 * room that's gone, 403 when the caller isn't a member — the right shape for
 * an explicit "open group details" action.
 */
export const getGroup = (
  sessionToken: string,
  id: string,
  signal?: AbortSignal
) =>
  chatFetch<ChatGroup, "json">({
    baseUrl: chatBaseUrl(),
    path: `/groups/${encodeURIComponent(id)}`,
    method: "GET",
    headers: bearer(sessionToken),
    responseType: "json",
    transform: (data) => fromRaw(data as RawGroup),
    signal,
  })

/**
 * `PATCH /groups/:id` — rename a group. Owner-or-admin server-side (FAME-574):
 * 403 for plain members, 422 on a missing/invalid name.
 */
export const renameGroup = (
  sessionToken: string,
  id: string,
  name: string,
  signal?: AbortSignal
): Promise<void> =>
  chatFetch<void, "json">({
    baseUrl: chatBaseUrl(),
    path: `/groups/${encodeURIComponent(id)}`,
    method: "PATCH",
    headers: bearer(sessionToken),
    data: { name },
    signal,
  })

/**
 * `DELETE /groups/:id/members?member_ids=a,b` — remove one or more members
 * (FAME-408). Owner-or-admin (FAME-574; 403 otherwise), but never the owner;
 * 422 on an empty set. Allowed while archived — archive freezes posting, not
 * membership admin. The server also deletes each removed member's conversation
 * row, so the group leaves their list on reseed.
 */
export const removeGroupMembers = (
  sessionToken: string,
  id: string,
  memberIds: string[],
  signal?: AbortSignal
): Promise<void> =>
  chatFetch<void, "json">({
    baseUrl: chatBaseUrl(),
    path: `/groups/${encodeURIComponent(id)}/members?member_ids=${memberIds.map(encodeURIComponent).join(",")}`,
    method: "DELETE",
    headers: bearer(sessionToken),
    signal,
  })

/**
 * `POST /groups/:id/archive` — freeze the group (FAME-411). Owner-or-admin
 * (FAME-574; 403 otherwise), idempotent. While archived the server bounces
 * posts and rejects rename/add-member (409); unarchive lifts it all.
 */
export const archiveGroup = (
  sessionToken: string,
  id: string,
  signal?: AbortSignal
): Promise<void> =>
  chatFetch<void, "json">({
    baseUrl: chatBaseUrl(),
    path: `/groups/${encodeURIComponent(id)}/archive`,
    method: "POST",
    headers: bearer(sessionToken),
    signal,
  })

/** `POST /groups/:id/unarchive` — the inverse of `archiveGroup`. */
export const unarchiveGroup = (
  sessionToken: string,
  id: string,
  signal?: AbortSignal
): Promise<void> =>
  chatFetch<void, "json">({
    baseUrl: chatBaseUrl(),
    path: `/groups/${encodeURIComponent(id)}/unarchive`,
    method: "POST",
    headers: bearer(sessionToken),
    signal,
  })

/**
 * `PUT /groups/:id/admins/:user_id` — promote a member to admin (FAME-574).
 * Owner-or-admin, idempotent. Refusals are named in the body rather than a bare
 * 403: `not_a_member` (the target is not in the group), `forbidden` (the caller
 * does not manage the group).
 */
export const promoteGroupAdmin = (
  sessionToken: string,
  id: string,
  userId: string,
  signal?: AbortSignal
): Promise<void> =>
  chatFetch<void, "json">({
    baseUrl: chatBaseUrl(),
    path: `/groups/${encodeURIComponent(id)}/admins/${encodeURIComponent(userId)}`,
    method: "PUT",
    headers: bearer(sessionToken),
    signal,
  })

/**
 * `DELETE /groups/:id/admins/:user_id` — demote an admin back to member
 * (FAME-574). Owner-or-admin, idempotent. Named refusals: `owner_immutable`
 * (the owner cannot be demoted), `last_manager` (you cannot step down as the
 * only manager), `forbidden`.
 */
export const demoteGroupAdmin = (
  sessionToken: string,
  id: string,
  userId: string,
  signal?: AbortSignal
): Promise<void> =>
  chatFetch<void, "json">({
    baseUrl: chatBaseUrl(),
    path: `/groups/${encodeURIComponent(id)}/admins/${encodeURIComponent(userId)}`,
    method: "DELETE",
    headers: bearer(sessionToken),
    signal,
  })

/**
 * `POST /groups/:id/members` — add one or more members in a single request.
 * Owner-or-admin server-side (FAME-574; 403 for plain members), 422 on an
 * empty set.
 */
export const addGroupMembers = (
  sessionToken: string,
  id: string,
  memberIds: string[],
  signal?: AbortSignal
): Promise<void> =>
  chatFetch<void, "json">({
    baseUrl: chatBaseUrl(),
    path: `/groups/${encodeURIComponent(id)}/members`,
    method: "POST",
    headers: bearer(sessionToken),
    data: { member_ids: memberIds },
    signal,
  })
